import styled, { css } from 'styled-components';
import { palette } from '../../palette';
import { resolveResponsiveProp } from '../../styles';
import breakpoints from '../../styles/breakpoints';
import { px, pxToRem, rem } from '../../utils';
import { createShouldForwardProp } from '../utils';
import { BASE_TYPOGRAPHY_STYLES, FontColor } from './Text.constants';
function getCustomBreakpoint(ResponsiveObject) {
  return Object.keys(ResponsiveObject).find(breakpointKey => !['desktop', 'mobile'].includes(breakpointKey));
}
function getResolvedFontSizeValue(fontSize, theme, breakpointSpecificResolvedAs, isMobile) {
  if (fontSize && typeof fontSize === 'number') {
    return fontSize;
  }
  if (Array.isArray(fontSize)) {
    return fontSize[isMobile ? 0 : 1];
  }
  return theme.Typography.DEFAULT_FONT_SIZE[breakpointSpecificResolvedAs] ? theme.Typography.DEFAULT_FONT_SIZE[breakpointSpecificResolvedAs][isMobile ? 0 : 1] : theme.Typography.DEFAULT_FONT_SIZE.p[isMobile ? 0 : 1];
}
function resolveLineHeightAsObject(as, theme) {
  const asCustomQueryKey = getCustomBreakpoint(as);
  return {
    mobile: (theme.Typography.TEXT_TYPE_TO_LINE_HEIGHT_PX[as.mobile] || theme.Typography.TEXT_TYPE_TO_LINE_HEIGHT_PX.p)[0],
    desktop: (theme.Typography.TEXT_TYPE_TO_LINE_HEIGHT_PX[as.desktop] || theme.Typography.TEXT_TYPE_TO_LINE_HEIGHT_PX.p)[1],
    ...(asCustomQueryKey ? {
      [asCustomQueryKey]: (theme.Typography.TEXT_TYPE_TO_LINE_HEIGHT_PX[as[asCustomQueryKey]] || theme.Typography.TEXT_TYPE_TO_LINE_HEIGHT_PX.p)[0]
    } : {})
  };
}
function resolveLineHeightValue(lineHeightFromProps, as, theme) {
  const isAsObject = typeof as === 'object' && !Array.isArray(as) ? resolveLineHeightAsObject(as, theme) : theme.Typography.TEXT_TYPE_TO_LINE_HEIGHT_PX[as] || theme.Typography.TEXT_TYPE_TO_LINE_HEIGHT_PX.p;
  const lineHeight = lineHeightFromProps ?? (Array.isArray(as) ? [(theme.Typography.TEXT_TYPE_TO_LINE_HEIGHT_PX[as[0]] || theme.Typography.TEXT_TYPE_TO_LINE_HEIGHT_PX.p)[0], (theme.Typography.TEXT_TYPE_TO_LINE_HEIGHT_PX[as[1]] || theme.Typography.TEXT_TYPE_TO_LINE_HEIGHT_PX.p)[1]] : isAsObject);
  if (Array.isArray(lineHeight)) {
    const desktopValue = lineHeight[1];
    const mobileValue = lineHeight[0];
    return `line-height: ${typeof desktopValue === 'number' ? rem(desktopValue / theme.Typography.DEFAULT_FONT_SIZE.p[1]) : desktopValue};
    @media screen and (max-width: ${px(breakpoints.mobileMax)}) {
      line-height: ${typeof mobileValue === 'number' ? rem(mobileValue / theme.Typography.DEFAULT_FONT_SIZE.p[0]) : mobileValue};
    }`;
  }
  if (typeof lineHeight === 'object') {
    const customMediaQuery = getCustomBreakpoint(lineHeight);
    const lineHeightRem = {
      mobile: typeof lineHeight.mobile === 'number' ? rem(lineHeight.mobile / theme.Typography.DEFAULT_FONT_SIZE.p[0]) : lineHeight.mobile,
      desktop: typeof lineHeight.desktop === 'number' ? rem(lineHeight.desktop / theme.Typography.DEFAULT_FONT_SIZE.p[1]) : lineHeight.desktop,
      ...(customMediaQuery ? {
        [customMediaQuery]: typeof lineHeight[customMediaQuery] === 'number' ? rem(pxToRem(lineHeight[customMediaQuery], theme)) : lineHeight[customMediaQuery]
      } : {})
    };
    return resolveResponsiveProp('lineHeight', lineHeightRem).join('');
  }
  return `line-height: ${typeof lineHeight === 'number' ? rem(pxToRem(lineHeight, theme)) : lineHeight};`;
}
function resolveColor(propColor, theme) {
  if (Object.values(FontColor).includes(propColor)) {
    return theme.Typography.FONT_COLOR_TO_COLOR[propColor];
  }
  return palette[propColor];
}
function resolveTextColorObject(color, theme) {
  const customColorQueryKey = getCustomBreakpoint(color);
  return {
    mobile: resolveColor(color.mobile, theme),
    desktop: resolveColor(color.desktop, theme),
    ...(customColorQueryKey ? {
      [customColorQueryKey]: resolveColor(color[customColorQueryKey], theme)
    } : {})
  };
}
function resolveTextColor(color, theme) {
  return Array.isArray(color) ? color.map(colorName => resolveColor(colorName, theme)) : typeof color === 'object' ? resolveTextColorObject(color, theme) : resolveColor(color, theme);
}
export const TextWrapper = styled.span.withConfig({
  shouldForwardProp: createShouldForwardProp(['fontWeight', 'fontSize', 'fontFamily'])
}).withConfig({
  componentId: "ds__sc-ec6uxn-0"
})(["", " ", ";", ";", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", ";"], BASE_TYPOGRAPHY_STYLES, _ref => {
  let {
    textColor,
    theme
  } = _ref;
  return textColor && resolveResponsiveProp('color', resolveTextColor(textColor, theme));
}, _ref2 => {
  let {
    align
  } = _ref2;
  return align && resolveResponsiveProp('textAlign', align);
}, _ref3 => {
  let {
    theme,
    styledAs
  } = _ref3;
  const resolvedAs = styledAs;
  const isResolvedAsArray = Array.isArray(resolvedAs);
  const isResolvedAsObject = typeof resolvedAs === 'object';
  const resolveStringFontFamily = !isResolvedAsArray && theme.Typography.TEXT_TYPE_TO_FONT_FAMILY_OVERRIDE[resolvedAs] !== undefined ? resolveResponsiveProp('fontFamily', theme.Typography.TEXT_TYPE_TO_FONT_FAMILY_OVERRIDE[resolvedAs]) : '';
  function resolveFontFamilyObject(fontFamilyObject) {
    const fontFamilyCustomKey = getCustomBreakpoint(fontFamilyObject);
    return resolveResponsiveProp('fontFamily', {
      mobile: theme.Typography.TEXT_TYPE_TO_FONT_FAMILY_OVERRIDE[fontFamilyObject.mobile],
      desktop: theme.Typography.TEXT_TYPE_TO_FONT_FAMILY_OVERRIDE[fontFamilyObject.desktop],
      ...(fontFamilyCustomKey ? {
        [fontFamilyCustomKey]: theme.Typography.TEXT_TYPE_TO_FONT_FAMILY_OVERRIDE[fontFamilyObject[fontFamilyCustomKey]]
      } : {})
    }, true);
  }
  const fontFamily = isResolvedAsArray ? theme.Typography.TEXT_TYPE_TO_FONT_FAMILY_OVERRIDE[resolvedAs[0]] !== undefined && theme.Typography.TEXT_TYPE_TO_FONT_FAMILY_OVERRIDE[resolvedAs[1]] !== undefined ? resolveResponsiveProp('fontFamily', [theme.Typography.TEXT_TYPE_TO_FONT_FAMILY_OVERRIDE[resolvedAs[0]], theme.Typography.TEXT_TYPE_TO_FONT_FAMILY_OVERRIDE[resolvedAs[1]]]) : '' : isResolvedAsObject ? resolveFontFamilyObject(resolvedAs) : resolveStringFontFamily;
  const resolveLetterSpacingString = !isResolvedAsArray && theme.Typography.TEXT_TYPE_TO_LETTER_SPACING[resolvedAs] !== undefined ? resolveResponsiveProp('letterSpacing', theme.Typography.TEXT_TYPE_TO_LETTER_SPACING[resolvedAs]) : 'letter-spacing: 0;';
  function resolveLetterSpacingObject(LetterSpacingObject) {
    const letterSpacingCustomKey = getCustomBreakpoint(LetterSpacingObject);
    return resolveResponsiveProp('letterSpacing', {
      mobile: theme.Typography.TEXT_TYPE_TO_LETTER_SPACING[LetterSpacingObject.mobile],
      desktop: theme.Typography.TEXT_TYPE_TO_LETTER_SPACING[LetterSpacingObject.desktop],
      ...(letterSpacingCustomKey ? {
        [letterSpacingCustomKey]: theme.Typography.TEXT_TYPE_TO_LETTER_SPACING[LetterSpacingObject[letterSpacingCustomKey]]
      } : {})
    }, true);
  }
  const letterSpacing = isResolvedAsArray ? theme.Typography.TEXT_TYPE_TO_LETTER_SPACING[resolvedAs[0]] !== undefined && theme.Typography.TEXT_TYPE_TO_LETTER_SPACING[resolvedAs[1]] !== undefined ? resolveResponsiveProp('letterSpacing', [theme.Typography.TEXT_TYPE_TO_LETTER_SPACING[resolvedAs[0]], theme.Typography.TEXT_TYPE_TO_LETTER_SPACING[resolvedAs[1]]]) : 'letter-spacing: 0;' : isResolvedAsObject ? resolveLetterSpacingObject(resolvedAs) : resolveLetterSpacingString;
  const resolveFontWeightString = !isResolvedAsArray && theme.Typography.TEXT_TYPE_TO_FONT_WEIGHT[resolvedAs] !== undefined ? resolveResponsiveProp('fontWeight', theme.Typography.TEXT_TYPE_TO_FONT_WEIGHT[resolvedAs]) : '';
  function resolveFontWeightObject(fontWeightObject) {
    const fontWeightCustomKey = getCustomBreakpoint(fontWeightObject);
    return resolveResponsiveProp('fontWeight', {
      mobile: theme.Typography.TEXT_TYPE_TO_FONT_WEIGHT[fontWeightObject.mobile],
      desktop: theme.Typography.TEXT_TYPE_TO_FONT_WEIGHT[fontWeightObject.desktop],
      ...(fontWeightCustomKey ? {
        [fontWeightCustomKey]: theme.Typography.TEXT_TYPE_TO_FONT_WEIGHT[fontWeightObject[fontWeightCustomKey]]
      } : {})
    }, true);
  }
  const fontWeight = isResolvedAsArray ? theme.Typography.TEXT_TYPE_TO_FONT_WEIGHT[resolvedAs[0]] !== undefined && theme.Typography.TEXT_TYPE_TO_FONT_WEIGHT[resolvedAs[1]] !== undefined ? resolveResponsiveProp('fontWeight', [theme.Typography.TEXT_TYPE_TO_FONT_WEIGHT[resolvedAs[0]], theme.Typography.TEXT_TYPE_TO_FONT_WEIGHT[resolvedAs[1]]]) : '' : isResolvedAsObject ? resolveFontWeightObject(resolvedAs) : resolveFontWeightString;
  return css(["", "", "", ""], fontFamily, letterSpacing, fontWeight);
}, _ref4 => {
  let {
    fontWeight
  } = _ref4;
  return fontWeight && resolveResponsiveProp('fontWeight', fontWeight).join('');
}, _ref5 => {
  let {
    fontFamily
  } = _ref5;
  return fontFamily && resolveResponsiveProp('fontFamily', fontFamily).join('');
}, _ref6 => {
  let {
    marginVertical
  } = _ref6;
  return marginVertical !== undefined && css(["", ";", ";"], resolveResponsiveProp('marginTop', marginVertical), resolveResponsiveProp('marginBottom', marginVertical));
}, _ref7 => {
  let {
    marginHorizontal
  } = _ref7;
  return marginHorizontal !== undefined && css(["", ";", ";"], resolveResponsiveProp('marginLeft', marginHorizontal), resolveResponsiveProp('marginRight', marginHorizontal));
}, _ref8 => {
  let {
    paddingVertical
  } = _ref8;
  return paddingVertical !== undefined && css(["", ";", ";"], resolveResponsiveProp('paddingTop', paddingVertical), resolveResponsiveProp('paddingBottom', paddingVertical));
}, _ref9 => {
  let {
    paddingHorizontal
  } = _ref9;
  return paddingHorizontal !== undefined && css(["", ";", ";"], resolveResponsiveProp('paddingLeft', paddingHorizontal), resolveResponsiveProp('paddingRight', paddingHorizontal));
}, _ref10 => {
  let {
    lineClamp
  } = _ref10;
  return lineClamp !== undefined && css(["display:-webkit-box;-webkit-box-orient:vertical;", ""], resolveResponsiveProp('WebkitLineClamp', lineClamp));
}, _ref11 => {
  let {
    marginTop
  } = _ref11;
  return marginTop !== undefined && resolveResponsiveProp('marginTop', marginTop);
}, _ref12 => {
  let {
    marginRight
  } = _ref12;
  return marginRight !== undefined && resolveResponsiveProp('marginRight', marginRight);
}, _ref13 => {
  let {
    marginBottom
  } = _ref13;
  return marginBottom !== undefined && resolveResponsiveProp('marginBottom', marginBottom);
}, _ref14 => {
  let {
    marginLeft
  } = _ref14;
  return marginLeft !== undefined && resolveResponsiveProp('marginLeft', marginLeft);
}, _ref15 => {
  let {
    paddingTop
  } = _ref15;
  return paddingTop !== undefined && resolveResponsiveProp('paddingTop', paddingTop);
}, _ref16 => {
  let {
    paddingRight
  } = _ref16;
  return paddingRight !== undefined && resolveResponsiveProp('paddingRight', paddingRight);
}, _ref17 => {
  let {
    paddingBottom
  } = _ref17;
  return paddingBottom !== undefined && resolveResponsiveProp('paddingBottom', paddingBottom);
}, _ref18 => {
  let {
    paddingLeft
  } = _ref18;
  return paddingLeft !== undefined && resolveResponsiveProp('paddingLeft', paddingLeft);
}, _ref19 => {
  let {
    isInlined
  } = _ref19;
  return isInlined && 'display: inline;';
}, _ref20 => {
  let {
    isBreakWord
  } = _ref20;
  return isBreakWord && 'overflow-wrap: break-word; word-wrap: break-word; hyphens: auto;';
}, _ref21 => {
  let {
    isTruncated,
    lineClamp
  } = _ref21;
  return isTruncated && css(["overflow:hidden;", ";text-overflow:ellipsis;"], lineClamp === undefined && 'white-space: nowrap;');
}, _ref22 => {
  let {
    textTransform
  } = _ref22;
  return textTransform && css(["text-transform:", ";"], textTransform);
}, _ref23 => {
  let {
    theme,
    styledAs,
    fontSize,
    lineHeight
  } = _ref23;
  const resolvedAs = styledAs;
  const isResolvedAsArray = Array.isArray(resolvedAs);
  const isResolvedAsObject = !isResolvedAsArray && typeof resolvedAs === 'object';
  let fontSizeProperty = '';
  let customResolvedAs = '';
  if (isResolvedAsObject) {
    customResolvedAs = getCustomBreakpoint(resolvedAs);
  }
  if (!Array.isArray(fontSize) && typeof fontSize === 'object') {
    fontSizeProperty = resolveResponsiveProp('fontSize', fontSize).join('');
  } else {
    const mobileResolvedAs = isResolvedAsArray ? resolvedAs[0] : isResolvedAsObject ? resolvedAs.mobile : resolvedAs;
    const textFontSizeMobile = getResolvedFontSizeValue(fontSize, theme, mobileResolvedAs, true);
    const desktopResolvedAs = isResolvedAsArray ? resolvedAs[1] : isResolvedAsObject ? resolvedAs.desktop : resolvedAs;
    const textFontSizeDesktop = getResolvedFontSizeValue(fontSize, theme, desktopResolvedAs, false);
    fontSizeProperty = textFontSizeDesktop ? `font-size: ${rem(pxToRem(textFontSizeDesktop, theme))};
        @media screen and (max-width: ${px(breakpoints.mobileMax)}) {
          font-size: ${rem(textFontSizeMobile / theme.Typography.DEFAULT_FONT_SIZE.p[1])};
        }` : '';
    if (isResolvedAsObject && customResolvedAs) {
      const fontSizeValue = theme.Typography.DEFAULT_FONT_SIZE[resolvedAs[customResolvedAs]][1] ?? theme.Typography.DEFAULT_FONT_SIZE.p[1];
      fontSizeProperty += `
        @media ${customResolvedAs} {
          font-size: ${rem(pxToRem(fontSizeValue, theme))};
        }`;
    }
  }
  const lineHeightProperty = resolveLineHeightValue(lineHeight, resolvedAs, theme);
  return `${fontSizeProperty}${lineHeightProperty}`;
});